import { Option } from '@sapphire/result';
import { Nullable } from '@ps/utils';
import {
  mapSurveyLocalization,
  SurveyLocaleData,
  SurveyMetadataFormState,
} from '@ps/survey-utils';
import {
  DEFAULT_CHILD_AUDIENCE_STATE,
  DEFAULT_SURVEY_CHILD_KEY,
} from '../../survey-creation-state.constants';
import {
  SurveyCreationAudienceState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';

export function deriveChildKey(
  surveyId: Nullable<number>,
  locale?: SurveyLocaleData,
): SurveyChildKey {
  return (
    surveyId ??
    (locale
      ? mapSurveyLocalization(locale.countryCode, locale.languageShortCode)
      : '')
  );
}

export function resolveActiveChildKey(
  state: SurveyCreationAudienceState,
): SurveyChildKey {
  return (
    state.activeChildId ??
    Object.keys(state.children)[0] ??
    DEFAULT_SURVEY_CHILD_KEY
  );
}

export function resolveTargetChildKeys(
  state: SurveyCreationAudienceState,
): SurveyChildKey[] {
  return Option.from(state.activeChildId)
    .map((activeChildId) => [activeChildId])
    .unwrapOr(Object.keys(state.children));
}

export type ChildPatch =
  | Partial<SurveyCreationChildAudienceState>
  | ((
      child: SurveyCreationChildAudienceState,
    ) => Partial<SurveyCreationChildAudienceState>);

export function patchChildren(
  state: SurveyCreationAudienceState,
  patch: ChildPatch,
): SurveyCreationAudienceState {
  const patchFor = typeof patch === 'function' ? patch : () => patch;

  return {
    ...state,
    children: {
      ...state.children,
      ...Object.fromEntries(
        resolveTargetChildKeys(state).map((key) => {
          const child = state.children[key] ?? {
            ...DEFAULT_CHILD_AUDIENCE_STATE,
            key,
          };
          return [key, { ...child, ...patchFor(child) }];
        }),
      ),
    },
  };
}

export function buildPreDraftChildren(
  children: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
  localeData: SurveyLocaleData[],
): Record<SurveyChildKey, SurveyCreationChildAudienceState> {
  const sharedMetadata =
    Object.values(children)[0]?.metadata ??
    DEFAULT_CHILD_AUDIENCE_STATE.metadata;

  const nextChildren = !localeData.length
    ? buildSingleDefaultChild(children, sharedMetadata)
    : Object.fromEntries(
        localeData.map((locale) => {
          const key = deriveChildKey(null, locale);
          return [
            key,
            children[key] ?? {
              ...DEFAULT_CHILD_AUDIENCE_STATE,
              key,
              metadata: filterMetadataToLocale(
                sharedMetadata,
                locale.countryCode,
                locale.languageShortCode,
              ),
            },
          ];
        }),
      );

  return normalizeSelected(nextChildren);
}

function buildSingleDefaultChild(
  children: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
  metadata: SurveyMetadataFormState,
): Record<SurveyChildKey, SurveyCreationChildAudienceState> {
  const key = DEFAULT_SURVEY_CHILD_KEY;
  return {
    [key]: children[key] ?? { ...DEFAULT_CHILD_AUDIENCE_STATE, key, metadata },
  };
}

function normalizeSelected(
  children: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
): Record<SurveyChildKey, SurveyCreationChildAudienceState> {
  const keys = Object.keys(children);

  if (keys.length === 1) {
    const [key] = keys;
    return { [key]: { ...children[key], selected: null } };
  }

  return Object.fromEntries(
    keys.map((key) => [
      key,
      { ...children[key], selected: children[key].selected ?? true },
    ]),
  );
}

function filterMetadataToLocale(
  metadata: SurveyMetadataFormState,
  countryCode: string,
  languageShortCode: string,
): SurveyMetadataFormState {
  return {
    ...metadata,
    value: metadata.value && {
      ...metadata.value,
      selectedCountries: [countryCode],
      selectedLanguages: [languageShortCode],
    },
  };
}

export function filterMetadataForChild(
  metadata: SurveyMetadataFormState,
  child: SurveyCreationChildAudienceState,
): SurveyMetadataFormState {
  return Option.from(localeOfChildKey(child.key))
    .map((locale) =>
      filterMetadataToLocale(
        metadata,
        locale.countryCode,
        locale.languageShortCode,
      ),
    )
    .unwrapOr(metadata);
}

function localeOfChildKey(
  key: SurveyChildKey,
): { countryCode: string; languageShortCode: string } | null {
  if (typeof key !== 'string' || !key.includes('_')) {
    return null;
  }

  const [languageShortCode, countryCode] = key.split('_');
  return { countryCode, languageShortCode };
}
